import { rasterSrc } from "@/generated/raster-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import {
  Section,
  SectionHeader,
  HomeGroupGrid,
  PillarTrio,
  SafeHandsBand,
  HowWeSupportYou,
  AccreditationStrip,
  AwardsRow,
  TestimonialCarousel,
  CTABand,
} from "@/components/site-blocks";
import { HomeFinanceDetails } from "@/components/home-finance-details";
import { HomeHeroSection } from "@/components/home-hero-section";
import { HomeLeadMagnets } from "@/components/home-lead-magnets";
import { HomeRoutePicker } from "@/components/home-route-picker";
import { HomeServicesAccordion } from "@/components/home-services-accordion";

import { COVERAGE_AREAS } from "@/lib/site";
import heroImageDesktopAvif from "@/assets/hero/crs-office-1920.avif";
import { pageSeo } from "@/lib/seo";

export const Route = createFileRoute("/")({
  head: () => {
    const seo = pageSeo({
      path: "/",
      title: "CRS Energy | Complete Remedial Solutions South Wales",
      description:
        "Insulation, damp remediation, structural repairs, heating and renewables across Wales and the South West. 50+ years of combined experience. Fully accredited.",
    });
    return {
      ...seo,
      links: [
        ...(seo.links ?? []),
        // Preload hero image for tablet/desktop only (mobile uses textured navy, no photo)
        {
          rel: "preload",
          as: "image",
          href: heroImageDesktopAvif,
          type: "image/avif",
          fetchPriority: "high",
          media: "(min-width: 768px)",
        },
      ],
    };
  },
  component: HomePage,
});

function HomePage() {
  return (
    <>
      <HomeHeroSection />

      <Section>
        <SectionHeader eyebrow="What we do" title="Our services" description="From cavity wall insulation removal to solar PV. Every measure your property needs." />
        <HomeServicesAccordion />
        <div className="mt-8 flex items-center justify-center">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-semibold shadow-cta hover:bg-accent/90 transition-colors"
          >
            View Full Service Catalogue <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Section>

      <Section className="bg-card">
        <SectionHeader
          eyebrow="Why CRS"
          title="Right Tools. Right Price. Right Experience."
          description="The contractor behind the check: fully certified, fully insured, and trusted across Wales and the South West."
        />
        <PillarTrio />
        <div className="mt-10">
          <SafeHandsBand />
        </div>
        <div className="mt-8 text-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-7 py-4 font-semibold shadow-cta hover:bg-accent/90 transition-colors"
          >
            Get an Expert In <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Section>

      <Section>
        <SectionHeader
          eyebrow="Our Support"
          title="How CRS Energy Can Support You"
          description="From funding to installation, we deliver every step of your home energy upgrade."
        />
        <HowWeSupportYou />
      </Section>

      <HomeRoutePicker />

      <Section className="bg-card">
        <HomeFinanceDetails />
      </Section>

      <Section className="py-12">
        <div className="flex flex-col gap-4 items-start">
          <SectionHeader eyebrow="Coverage" title="Areas we cover" description="Headquartered in Bridgend, with field teams across Wales and the South West." />
          <ul className="flex flex-wrap gap-2">
            {COVERAGE_AREAS.map((area) => (
              <li key={area} className="rounded-full bg-card border border-border px-3 py-1 text-sm text-foreground">
                {area}
              </li>
            ))}
          </ul>
          <Link to="/coverage" className="inline-flex items-center gap-1 font-semibold text-accent">
            View full coverage <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Section>

      <Section className="bg-card">
        <SectionHeader align="center" eyebrow="Accreditations" title="Fully accredited, fully insured" />
        <AccreditationStrip />
      </Section>

      <Section>
        <SectionHeader eyebrow="Awards" title="Recognised work" />
        <AwardsRow />
      </Section>

      <Section className="bg-primary-soft">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-accent mb-3">For trade & insurers</p>
            <h2 className="text-3xl md:text-4xl font-bold text-balance">Working with insurers, housing associations and local authorities.</h2>
            <p className="mt-4 text-muted-foreground">Single property to portfolio-wide remediation, insulation and energy-efficiency programmes: delivered to your scope and timelines.</p>
            <Link to="/for-trade" className="mt-5 inline-flex items-center gap-1 font-semibold text-accent">
              Learn about trade partnerships <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <img
            src={rasterSrc("stock/photo-1581094288338-2314dddb7ece")}
            alt="Contractor team coordinating on site"
            className="rounded-2xl shadow-lift w-full h-72 object-cover"
            loading="lazy"
          />
        </div>
      </Section>

      <Section className="bg-card">
        <SectionHeader
          eyebrow="The Home Check Group"
          title="Part of a complete home services group"
          description="CRS works alongside four specialist diagnostic brands. They identify the problem. We deliver the solution."
        />
        <HomeGroupGrid />
        <div className="mt-8 text-center">
          <Link to="/the-home-check-group" className="inline-flex items-center gap-1 font-semibold text-accent">
            Learn how the Home Check Group works <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Section>

      <Section>
        <SectionHeader
          eyebrow="Reviews"
          title="What our customers say"
          description="Real feedback from homeowners across Wales and the South West."
        />
        <TestimonialCarousel />
      </Section>

      <HomeLeadMagnets />

      <CTABand
        title="Ready to get started?"
        description="Speak to the team for a free, no-obligation survey across Wales and the South West."
      />
    </>
  );
}
